import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import {
  FONT,
  ML,
  thb,
  fmtD,
  drawSectionTitle,
  drawField,
  ensureSpace,
  resolveStart,
  calcInterest,
  DEFAULT_INTEREST_RATE,
} from "./pdfHelpers";
import { IMAGE_TYPES, MENU_IMAGE_MAP } from "../constants";

const DEFAULT_COMPANY = "บริษัท วัน เอเอ็มซี จำกัด (สำนักงานใหญ่)";
const DEFAULT_COMPANY_ADDRESS =
  "89 หมู่ 14 ถ.มิตรภาพ ต.ในเมือง อ.เมืองขอนแก่น จ.ขอนแก่น 40000";

// ช่องติ๊ก ☐ / ☑ (วาดเองเพราะฟอนต์ไม่มีสัญลักษณ์)
function drawCheckbox(doc, x, y, checked) {
  const s = 4;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.rect(x, y - s + 0.8, s, s);
  if (checked) {
    doc.setLineWidth(0.6);
    doc.line(x + 0.8, y - 1.2, x + 1.7, y + 0.1);
    doc.line(x + 1.7, y + 0.1, x + 3.4, y - 2.6);
  }
  doc.setLineWidth(0.2);
}

function drawCheckItem(doc, x, y, label, checked) {
  drawCheckbox(doc, x, y, checked);
  doc.setFont(FONT, "normal");
  doc.setFontSize(14);
  doc.text(label, x + 6, y);
  return y + 8;
}

// label: value ที่ตัดบรรทัดให้อัตโนมัติเมื่อข้อความยาว
function drawWrapField(doc, x, y, maxX, label, value) {
  doc.setFont(FONT, "bold");
  doc.setFontSize(14);
  doc.text(label + ":", x, y);
  const vx = x + doc.getTextWidth(label + ":") + 2;
  doc.setFont(FONT, "normal");
  const lines = doc.splitTextToSize(String(value ?? "-"), maxX - vx);
  doc.text(lines, vx, y);
  return y + lines.length * 6 + 2;
}

export function generateCoverSheetPdf({
  result,
  detail,
  item,
  accounts = [],
  contracts = [],
  calcDate,
  startDate,
  interestRate = DEFAULT_INTEREST_RATE,
  files = [],
  operatorNote,
  responsible,
}) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const MR = pageW - ML;
  const contentW = MR - ML;

  const collaterals = detail?.stock_collaterals ?? [];
  const notes = detail?.notes ?? [];
  const nationalId = detail?.customers?.[0]?.citizen_id;
  // วันที่คิดดอกเบี้ยถึง (จาก DatePicker, ไม่ส่งมาใช้วันนี้)
  const asOf = calcDate ?? dayjs();
  // map account_no → overdue (ใช้หา due_date ล่าสุดของแต่ละสัญญา)
  const overdueByAccount = Object.fromEntries(
    accounts.map((ac) => [ac.account_no, ac.overdue ?? []]),
  );

  // จำนวนไฟล์แนบแยกตาม imageType
  const fileCount = files.reduce((acc, f) => {
    acc[f.imageType] = (acc[f.imageType] ?? 0) + 1;
    return acc;
  }, {});

  // ── หัวเรื่อง ──
  let y = 18;
  doc.setFont(FONT, "bold");
  doc.setFontSize(24);
  doc.text("ใบปะหน้า", pageW / 2, y, { align: "center" });
  y += 4;
  doc.setDrawColor(50, 50, 50);
  doc.setLineWidth(0.5);
  doc.line(ML, y, MR, y);
  doc.setLineWidth(0.2);
  y += 9;

  // ── โจทย์ ──
  y = drawSectionTitle(doc, ML, y, contentW, "โจทก์");
  y = drawField(
    doc,
    ML,
    y,
    "ชื่อบริษัท",
    result?.company_name ?? DEFAULT_COMPANY,
  );
  const addrLabel = "ที่อยู่:";
  doc.setFont(FONT, "bold");
  doc.text(addrLabel, ML, y);
  const addrX = ML + doc.getTextWidth(addrLabel) + 2;
  doc.setFont(FONT, "normal");
  const addrLines = doc.splitTextToSize(
    result?.company_address ?? DEFAULT_COMPANY_ADDRESS,
    MR - addrX,
  );
  doc.text(addrLines, addrX, y);
  y += addrLines.length * 6 + 5;

  // ── ฟ้องประเภท (ประเภทเอกสารชั้นฟ้อง) ──
  y = drawSectionTitle(doc, ML, y, contentW, "ฟ้องประเภท");
  const suitTypes = IMAGE_TYPES.filter((t) =>
    MENU_IMAGE_MAP[11].includes(t.id),
  );
  suitTypes.forEach((t) => {
    y = drawCheckItem(doc, ML + 2, y, t.label, !!fileCount[t.id]);
  });
  y += 3;

  // ── จำเลย ──
  y = drawSectionTitle(doc, ML, y, contentW, "จำเลย");
  y = drawField(doc, ML, y, "ชื่อ-นามสกุล", result?.debtor_name);
  y = drawField(doc, ML, y, "รหัสบัตรประชาชน", nationalId);
  y = drawField(doc, ML, y, "Borr Code", result?.borr_code ?? item?.borr_code);
  y += 3;

  // ── หลักประกัน ──
  y = ensureSpace(doc, y, 30);
  y = drawSectionTitle(doc, ML, y, contentW, "หลักประกัน");
  if (collaterals.length > 0) {
    collaterals.forEach((s, i) => {
      y = ensureSpace(doc, y, 16);
      const prefix = collaterals.length > 1 ? `${i + 1}. ` : "";
      y = drawWrapField(
        doc,
        ML,
        y,
        MR,
        `${prefix}${s.collateral_type ?? "-"}`,
        `${s.location ?? "-"} (ราคาประเมิน ${thb(s.latest_appraisal)} บาท)`,
      );
    });
  } else {
    y = drawField(doc, ML, y, "หลักประกัน", "-");
  }
  y += 3;

  // ── สัญญาสินเชื่อ (สรุปยอด) ──
  y = ensureSpace(doc, y, 40);
  y = drawSectionTitle(doc, ML, y, contentW, "สัญญาสินเชื่อ");
  const oneInterest = (a) =>
    calcInterest(
      a.principal_amount,
      resolveStart(overdueByAccount[a.account_no], startDate),
      asOf,
      interestRate,
    );
  const sumPrincipal = contracts.reduce(
    (s, a) => s + Number(a.principal_amount || 0),
    0,
  );
  const sumInterest = contracts.reduce(
    (s, a) => s + Number(a.interest || 0) + oneInterest(a),
    0,
  );
  // 2 คอลัมน์: ซ้าย = บัญชี/วันที่คำนวณ, ขวา = ยอดเงิน
  const colW = contentW / 2;
  const RX = ML + colW + 4;
  let ly = drawWrapField(
    doc,
    ML,
    y,
    RX - 6,
    "เลขที่บัญชี",
    contracts.map((a) => a.account_no ?? "-").join(", ") || "-",
  );
  ly = drawField(doc, ML, ly, "คำนวณดอกเบี้ยถึงวันที่", fmtD(asOf));
  let ry = drawField(doc, RX, y, "เงินต้น", `${thb(sumPrincipal)} บาท`);
  ry = drawField(doc, RX, ry, "ดอกเบี้ย", `${thb(sumInterest)} บาท`);
  ry = drawField(
    doc,
    RX,
    ry,
    "รวมทั้งหมด",
    `${thb(sumPrincipal + sumInterest)} บาท`,
  );
  y = Math.max(ly, ry) + 3;

  // ── หมายเหตุจากผู้ปฏิบัติงาน ──
  y = ensureSpace(doc, y, 30);
  y = drawSectionTitle(doc, ML, y, contentW, "หมายเหตุจากผู้ปฏิบัติงาน");
  const noteRows = notes.map((n) => [
    n.status ?? "-",
    n.note ?? "-",
    n.note_by ?? "-",
    fmtD(n.updated_at),
  ]);
  if (operatorNote) {
    noteRows.push(["-", operatorNote, responsible ?? "-", fmtD(dayjs())]);
  }
  if (noteRows.length > 0) {
    autoTable(doc, {
      startY: y,
      margin: { left: ML, right: ML },
      theme: "grid",
      styles: {
        font: FONT,
        fontSize: 13,
        textColor: [0, 0, 0],
        lineColor: [80, 80, 80],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 35 },
        2: { cellWidth: 30 },
        3: { cellWidth: 25 },
      },
      head: [["สถานะ", "หมายเหตุ", "บันทึกโดย", "วันที่บันทึก"]],
      body: noteRows,
    });
    y = doc.lastAutoTable.finalY + 8;
  } else {
    doc.setFont(FONT, "normal");
    doc.setFontSize(14);
    doc.text("ยังไม่มีบันทึก", ML, y);
    y += 10;
  }

  // ── เอกสารแนบ ──
  y = ensureSpace(doc, y, 10 + IMAGE_TYPES.length * 8);
  y = drawSectionTitle(doc, ML, y, contentW, "เอกสารแนบ");
  IMAGE_TYPES.forEach((t) => {
    const count = fileCount[t.id] ?? 0;
    y = drawCheckItem(
      doc,
      ML + 2,
      y,
      count ? `${t.label} (${count} ไฟล์)` : t.label,
      count > 0,
    );
  });

  // วันที่พิมพ์ (พ.ศ.) + ผู้รับผิดชอบ มุมขวาบนของทุกหน้า
  const pageCount = doc.internal.getNumberOfPages();
  const now = dayjs();
  const printedOn = `วันที่พิมพ์ ${now.date()}/${now.month() + 1}/${now.year() + 543}`;
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFont(FONT, "normal");
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(printedOn, MR, 10, { align: "right" });
    doc.text(`ผู้รับผิดชอบ ${responsible || "-"}`, MR, 15, { align: "right" });
  }

  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  window.open(url);
}
